import { Chart } from 'chart.js/auto';
import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class CloudCeilingWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Clouds & Tops Elevation Trend';
    const height = widget.options?.chartHeight || 200;
    const mode = widget.options?.displayMode || 'both';
    const canvasId = `poi-cloud-canvas-${widget.id}`;
    const data = ctx.data;
    const isImperial = store.getState().units === 'imperial';
    const elevUnit = isImperial ? 'ft' : 'm';

    const fmtElev = (ft: number) => isImperial ? `${Math.round(ft).toLocaleString()} ${elevUnit}` : `${Math.round(ft / 3.28084).toLocaleString()} ${elevUnit}`;

    const poiElev = data.poiElev || data.forecastElev || 5000;
    const freezingElev = data.avgFreezingElev || 8500;
    const avgCover = data.cloudCover && data.cloudCover.length ? Math.round(data.cloudCover.reduce((a, b) => a + b, 0) / data.cloudCover.length) : 40;

    let baseElev = 6500;
    let topElev = 11500;
    if (data.soundings && data.soundings.length > 0) {
      const s0 = data.soundings[0];
      if (s0.inversionCapFt && s0.inversionCapFt > 0) topElev = s0.inversionCapFt;
      if (s0.surface) {
        const spread = Math.max(1, s0.surface.tempF - s0.surface.dewPointF);
        baseElev = Math.round(s0.surface.elevationFt + spread * 220);
        if (topElev <= baseElev) topElev = baseElev + 4000;
      }
    }

    const inDeck = poiElev >= baseElev && poiElev <= topElev;
    const aboveClouds = poiElev > topElev;
    const statusText = inDeck ? '⚠️ IN CLOUD DECK' : (aboveClouds ? '✨ ABOVE CLOUDS' : '🌤️ BELOW DECK');
    const statusColor = inDeck ? '#f43f5e' : (aboveClouds ? '#00d4ff' : '#10b981');

    const isBlockedByTier =
      (!data.timeLabels || data.timeLabels.length === 0) ||
      (!data.hasBinaryPointData && Boolean(data.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles')));

    let summaryHtml = '';
    if (!isBlockedByTier && (mode === 'both' || mode === 'summary')) {
      summaryHtml = `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px; padding: 8px 10px;">
            <div style="font-size: 0.68rem; color: #94a3b8; font-weight: 600;">Cloud Base (Ceiling)</div>
            <div style="font-size: 1.1rem; font-weight: 700; color: #38bdf8; font-family: monospace;">${fmtElev(baseElev)}</div>
          </div>
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px; padding: 8px 10px;">
            <div style="font-size: 0.68rem; color: #94a3b8; font-weight: 600;">Cloud Top Altitude</div>
            <div style="font-size: 1.1rem; font-weight: 700; color: #c084fc; font-family: monospace;">${fmtElev(topElev)}</div>
          </div>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(15, 23, 42, 0.4); border-radius: 8px; padding: 6px 10px; font-size: 0.72rem; margin-bottom: 8px;">
          <div>Freezing: <strong style="color: #67e8f9;">${fmtElev(freezingElev)}</strong> · Cover: <strong style="color: #38bdf8;">${avgCover}%</strong></div>
          <div style="font-weight: 700; color: ${statusColor}; font-size: 0.7rem;">${statusText}</div>
        </div>
      `;
    }

    let chartHtml = '';
    if (mode === 'both' || mode === 'chart') {
      chartHtml = `
        <div style="height: ${height}px; position: relative; width: 100%; margin-top: ${mode === 'both' ? '8px' : '4px'};">
          <canvas id="${canvasId}"></canvas>
        </div>
      `;
    }

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${title}</span>
        </div>
        ${summaryHtml}
        ${chartHtml}
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const mode = widget.options?.displayMode || 'both';
    if (mode === 'summary') return;

    const canvasId = `poi-cloud-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    const labels = data?.timeLabels || [];

    const isBlockedByTier =
      (!labels || labels.length === 0) ||
      (!data.hasBinaryPointData && Boolean(data.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles')));

    if (isBlockedByTier) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: 'cloud base, cloud top & freezing elevation trend charts'
        });
      }
      return;
    }

    const isImperial = store.getState().units === 'imperial';
    const fmtY = (val: number) => isImperial ? `${val.toLocaleString()} ft` : `${Math.round(val / 3.28084).toLocaleString()} m`;

    const baseElevEstimate = 6500;
    const topElevEstimate = 11500;

    const cloudTopSeries: number[] = [];
    const cloudBaseSeries: number[] = [];
    const freezingSeries: number[] = [];
    const coverSeries: number[] = data.cloudCover || [];

    labels.forEach((_, idx) => {
      let b = baseElevEstimate;
      let t = topElevEstimate;
      let frz = data.avgFreezingElev || 8500;

      if (data.soundings && data.soundings[idx]) {
        const s = data.soundings[idx];
        if (s.freezingLevelFt) frz = s.freezingLevelFt;
        if (s.inversionCapFt && s.inversionCapFt > 0) t = s.inversionCapFt;
        if (s.surface) {
          const spread = Math.max(1, s.surface.tempF - s.surface.dewPointF);
          b = Math.round(s.surface.elevationFt + spread * 220);
          if (t <= b) t = b + 3500;
        }
      }

      cloudTopSeries.push(t);
      cloudBaseSeries.push(b);
      freezingSeries.push(frz);
    });

    const topCol = widget.options?.topColor || '#93c5fd';
    const baseCol = widget.options?.baseColor || '#0284c7';
    const rawFill = widget.options?.fillColor || '#daf3fe';
    const fillAlpha = (widget.options?.fillOpacity !== undefined ? widget.options.fillOpacity : 22) / 100;
    let finalFillColor = 'rgba(218, 243, 254, 0.22)';
    if (rawFill.startsWith('rgba') || rawFill.startsWith('hsla')) {
      finalFillColor = rawFill;
    } else if (rawFill.startsWith('#')) {
      const hex = rawFill.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) || 218;
      const g = parseInt(hex.substring(2, 4), 16) || 243;
      const b = parseInt(hex.substring(4, 6), 16) || 254;
      finalFillColor = `rgba(${r}, ${g}, ${b}, ${fillAlpha})`;
    }

    const showFreez = widget.options?.showFreezingLine !== false;
    const showCover = widget.options?.showCloudCover !== false;

    const datasets: any[] = [
      {
        label: 'Cloud Top',
        data: cloudTopSeries,
        borderColor: topCol,
        backgroundColor: finalFillColor,
        borderWidth: 2,
        fill: '+1',
        tension: 0.35,
        pointRadius: 0,
        yAxisID: 'y'
      },
      {
        label: 'Cloud Base',
        data: cloudBaseSeries,
        borderColor: baseCol,
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        tension: 0.35,
        pointRadius: 0,
        yAxisID: 'y'
      }
    ];

    if (showFreez) {
      datasets.push({
        label: isImperial ? 'Freezing Level (32°F)' : 'Freezing Level (0°C)',
        data: freezingSeries,
        borderColor: '#38bdf8',
        borderDash: [6, 4],
        borderWidth: 1.5,
        fill: false,
        pointRadius: 0,
        yAxisID: 'y'
      });
    }

    if (showCover && coverSeries.length) {
      datasets.push({
        label: 'Cloud Cover (%)',
        data: coverSeries,
        borderColor: 'rgba(148, 163, 184, 0.85)',
        borderDash: [3, 3],
        borderWidth: 1.2,
        fill: false,
        pointRadius: 0,
        tension: 0.3,
        yAxisID: 'yCover'
      });
    }

    const peakElev = data.maxElev || (data.poiElev ? data.poiElev + 3000 : 12000);
    const poiElev = data.poiElev || data.forecastElev || 5000;
    const baseElev = data.baseElev || data.minElev || (data.poiElev ? Math.max(0, data.poiElev - 4000) : 2000);

    const minChartElev = baseElev >= 4000 ? 2000 : 0;
    const maxChartElev = Math.max(16000, peakElev + 1500);

    const showMountain = widget.options?.showMountainSilhouette !== false && widget.options?.showMountainBackdrop !== false;

    const mountainSilhouettePlugin = {
      id: 'mountainSilhouettePlugin',
      beforeDatasetsDraw(chartInstance: any) {
        if (!showMountain) return;
        const { ctx: chartCtx, chartArea, scales } = chartInstance;
        if (!chartArea || !scales || !scales.y || !scales.x) return;
        const { left, right, top, bottom } = chartArea;
        const width = right - left;
        const height = bottom - top;

        const yPeak = scales.y.getPixelForValue(peakElev);
        const yPoi = scales.y.getPixelForValue(poiElev);
        const yBase = scales.y.getPixelForValue(baseElev);

        chartCtx.save();
        chartCtx.beginPath();
        chartCtx.rect(left, top, width, height);
        chartCtx.clip();

        // 1. Repeating 500px Mountain Peaks across chart width
        const peakWidth = 500;
        const numPeaks = Math.ceil(width / peakWidth) + 1;

        const ridgePoints: { x: number; y: number }[] = [];
        const peakApices: { x: number; y: number }[] = [];
        const poiMarkers: { x: number; y: number }[] = [];
        const poiFactor = Math.max(0, Math.min(1, (poiElev - baseElev) / (peakElev - baseElev || 1)));

        ridgePoints.push({ x: left, y: yBase });

        for (let p = 0; p < numPeaks; p++) {
          const pLeft = left + p * peakWidth;
          const pRight = pLeft + peakWidth;
          const peakXRatio = (p % 2 === 0) ? 0.46 : 0.52;
          const curPeakX = pLeft + peakWidth * peakXRatio;
          const leftCol = pLeft + peakWidth * 0.05;
          const rightCol = pLeft + peakWidth * 0.95;

          if (curPeakX >= left - 50 && curPeakX <= right + 50) {
            peakApices.push({ x: curPeakX, y: yPeak });
          }

          const poiX = leftCol + (curPeakX - leftCol) * Math.pow(poiFactor, 1.0 / 1.6);
          if (poiX >= left - 50 && poiX <= right + 50) {
            poiMarkers.push({ x: poiX, y: yPoi });
          }

          ridgePoints.push({ x: leftCol, y: yBase });

          const numSlopePts = 24;
          for (let i = 1; i <= numSlopePts; i++) {
            const u = i / numSlopePts;
            const x = leftCol + (curPeakX - leftCol) * u;
            const factor = Math.min(1.0, Math.pow(u, 1.6) * 0.85 + (1 - Math.cos(u * Math.PI)) * 0.075 + Math.sin(u * Math.PI * 2) * 0.04);
            const elev = baseElev + (peakElev - baseElev) * factor;
            const y = scales.y.getPixelForValue(elev);
            ridgePoints.push({ x, y });
          }

          for (let i = 1; i <= numSlopePts; i++) {
            const v = i / numSlopePts;
            const x = curPeakX + (rightCol - curPeakX) * v;
            const factor = Math.max(0.0, (1 - Math.pow(v, 1.4)) * 0.88 + Math.sin((1 - v) * Math.PI) * 0.12);
            const elev = baseElev + (peakElev - baseElev) * factor;
            const y = scales.y.getPixelForValue(elev);
            ridgePoints.push({ x, y });
          }

          ridgePoints.push({ x: pRight, y: yBase });
        }

        // Fill mountain silhouette polygon
        chartCtx.beginPath();
        chartCtx.moveTo(left, bottom);
        for (let i = 0; i < ridgePoints.length; i++) {
          chartCtx.lineTo(ridgePoints[i].x, ridgePoints[i].y);
        }
        chartCtx.lineTo(right, yBase);
        chartCtx.lineTo(right, bottom);
        chartCtx.closePath();

        // Alpine Mountain Fill Gradient (Green -> Brown -> White Snow Cap)
        const mGrad = chartCtx.createLinearGradient(0, yPeak, 0, bottom);
        mGrad.addColorStop(0, 'rgba(255, 255, 255, 0.90)');   // Crisp snow cap summit
        mGrad.addColorStop(0.12, 'rgba(226, 232, 240, 0.75)'); // Glacier snow pack
        mGrad.addColorStop(0.28, 'rgba(139, 107, 79, 0.65)');  // Alpine granite brown rock/scree
        mGrad.addColorStop(0.48, 'rgba(92, 64, 51, 0.70)');    // Timberline crags
        mGrad.addColorStop(0.68, 'rgba(21, 128, 61, 0.60)');   // Subalpine evergreen pine
        mGrad.addColorStop(0.88, 'rgba(20, 83, 45, 0.75)');    // Valley forest
        mGrad.addColorStop(1, 'rgba(10, 31, 19, 0.90)');       // Valley base floor
        chartCtx.fillStyle = mGrad;
        chartCtx.fill();

        // Mountain Ridge Outline with illuminated crest gradient
        const rGrad = chartCtx.createLinearGradient(0, yPeak, 0, yBase);
        rGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');    // Sparkling snow ridge
        rGrad.addColorStop(0.25, 'rgba(241, 245, 249, 0.85)');  // Snowpack crest
        rGrad.addColorStop(0.5, 'rgba(212, 163, 115, 0.80)');   // Sunlit granite brown
        rGrad.addColorStop(0.8, 'rgba(34, 197, 94, 0.75)');     // Pine tree line
        rGrad.addColorStop(1, 'rgba(21, 128, 61, 0.70)');      // Valley base
        chartCtx.beginPath();
        chartCtx.moveTo(ridgePoints[0].x, ridgePoints[0].y);
        for (let i = 1; i < ridgePoints.length; i++) {
          chartCtx.lineTo(ridgePoints[i].x, ridgePoints[i].y);
        }
        chartCtx.strokeStyle = rGrad;
        chartCtx.lineWidth = 2;
        chartCtx.lineCap = 'round';
        chartCtx.lineJoin = 'round';
        chartCtx.stroke();

        // Reference Lines & Markers
        // 1. Peak Line (drawn across peak summit elevation)
        chartCtx.beginPath();
        chartCtx.setLineDash([8, 6]);
        chartCtx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        chartCtx.lineWidth = 1.5;
        chartCtx.moveTo(left, yPeak);
        chartCtx.lineTo(right, yPeak);
        chartCtx.stroke();

        // Summit apex markers for each 500px peak
        chartCtx.fillStyle = '#f8fafc';
        for (let i = 0; i < peakApices.length; i++) {
          chartCtx.beginPath();
          chartCtx.arc(peakApices[i].x, peakApices[i].y, 3.5, 0, Math.PI * 2);
          chartCtx.fill();
        }

        // 2. Base Line (drawn across base at valley elevation)
        chartCtx.beginPath();
        chartCtx.setLineDash([6, 5]);
        chartCtx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
        chartCtx.lineWidth = 1.5;
        chartCtx.moveTo(left, yBase);
        chartCtx.lineTo(right, yBase);
        chartCtx.stroke();

        // 3. POI Line (drawn across target POI elevation)
        chartCtx.beginPath();
        chartCtx.setLineDash([10, 6]);
        chartCtx.strokeStyle = 'rgba(251, 191, 36, 0.85)';
        chartCtx.lineWidth = 1.8;
        chartCtx.moveTo(left, yPoi);
        chartCtx.lineTo(right, yPoi);
        chartCtx.stroke();
        chartCtx.setLineDash([]);

        // POI waypoint markers on each peak's ridge
        for (let i = 0; i < poiMarkers.length; i++) {
          chartCtx.beginPath();
          chartCtx.arc(poiMarkers[i].x, poiMarkers[i].y, 4.5, 0, Math.PI * 2);
          chartCtx.fillStyle = '#fbbf24';
          chartCtx.fill();
          chartCtx.strokeStyle = '#78350f';
          chartCtx.lineWidth = 1.5;
          chartCtx.stroke();
        }

        // Reference Line Labels
        chartCtx.font = 'bold 9px Inter, sans-serif';
        chartCtx.textAlign = 'left';
        chartCtx.fillStyle = 'rgba(241, 245, 249, 0.85)';
        chartCtx.fillText(`Peak Elevation (${fmtY(peakElev)})`, left + 6, yPeak - 4);
        chartCtx.fillText(`Base Elevation (${fmtY(baseElev)})`, left + 6, yBase - 4);

        chartCtx.fillStyle = '#fbbf24';
        chartCtx.fillText(`POI Elevation (${fmtY(poiElev)})`, left + 6, yPoi - 4);

        chartCtx.restore();
      }
    };

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      plugins: [mountainSilhouettePlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: '#94a3b8',
              font: { size: 10, family: 'Inter' },
              boxWidth: 8,
              boxHeight: 8,
              padding: 10
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            callbacks: {
              afterTitle: (items) => {
                if (!items || !items.length) return '';
                const idx = items[0].dataIndex;
                const top = cloudTopSeries[idx] || 0;
                const base = cloudBaseSeries[idx] || 0;
                const inDeck = poiElev >= base && poiElev <= top;
                const above = poiElev > top;
                const status = inDeck ? '⚠️ In Cloud Deck' : (above ? '✨ Above Clouds' : '🌤️ Below Deck');
                return `POI (${fmtY(poiElev)}): ${status}`;
              },
              label: (item) => {
                if (item.dataset.yAxisID === 'yCover') return `${item.dataset.label}: ${item.raw}%`;
                return `${item.dataset.label}: ${fmtY(Number(item.raw))}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: {
              color: '#64748b',
              font: { size: 9 },
              maxTicksLimit: 8
            }
          },
          y: {
            position: 'left',
            min: minChartElev,
            max: maxChartElev,
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: {
              color: '#94a3b8',
              font: { size: 9 },
              stepSize: 2000,
              callback: (val) => fmtY(Number(val))
            }
          },
          yCover: {
            position: 'right',
            min: 0,
            max: 100,
            grid: { display: false },
            ticks: {
              color: '#64748b',
              font: { size: 9 },
              callback: (val) => `${val}%`
            }
          }
        }
      }
    });

    ctx.charts.push(chart);
  }
}
